import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import OTPVerification from './OTPVerification';
import toast from 'react-hot-toast';

const OTP_MAX_ATTEMPTS = 5;
const OTP_BLOCK_MS = 30 * 60 * 1000; // 30 minutes block after 5 attempts

const getOtpAttempts = (mobile) => {
  try {
    const raw = localStorage.getItem(`otp_limit_${mobile}`);
    if (!raw) return { count: 0, blockedAt: null };
    const parsed = JSON.parse(raw);
    // If blocked, check if 30 min has passed — if yes, reset
    if (parsed.blockedAt && Date.now() - parsed.blockedAt > OTP_BLOCK_MS) {
      localStorage.removeItem(`otp_limit_${mobile}`);
      return { count: 0, blockedAt: null };
    }
    return parsed;
  } catch {
    return { count: 0, blockedAt: null };
  }
};

const incrementOtpAttempts = (mobile) => {
  const current = getOtpAttempts(mobile);
  const newCount = current.count + 1;
  const updated = {
    count: newCount,
    blockedAt: newCount >= OTP_MAX_ATTEMPTS ? Date.now() : null,
  };
  localStorage.setItem(`otp_limit_${mobile}`, JSON.stringify(updated));
  return updated;
};

// Force Login Modal Component
const ForceLoginModal = ({ isOpen, onClose, onForceLogin, phoneNumber, loading }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative bg-slate-800 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-slate-700">
        {/* Warning Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-3">
          Session Already Active
        </h3>

        {/* Message */}
        <p className="text-gray-300 text-center mb-2">
          You are already logged in on another device.
        </p>
        <p className="text-gray-400 text-sm text-center mb-6">
          Phone: <span className="text-blue-400 font-medium">{phoneNumber}</span>
        </p>

        {/* Info Box */}
        <div className="bg-slate-700/50 rounded-xl p-4 mb-6">
          <p className="text-gray-300 text-sm text-center">
            If you continue, you will be logged out from the other device and a new session will be created here.
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-slate-700 hover:bg-slate-600 text-gray-200 font-semibold rounded-xl transition duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onForceLogin}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>Continue</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const MobileLoginTab = ({ onLoginSuccess }) => {
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);
  const [showForceLoginModal, setShowForceLoginModal] = useState(false);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [forceLoginLoading, setForceLoginLoading] = useState(false);

  // Handle Force Login - logout from other device and login here
  const handleForceLogin = async () => {
    setForceLoginLoading(true);
    
    try {
      const formattedPhoneNumber = `+91${mobile}`;
      console.log('=== FORCE LOGIN STARTED ===');
      console.log('Calling force-send API for:', formattedPhoneNumber);
      
      const response = await fetch(`${API_BASE_URL}/users/auth/otp/force-send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phNum: formattedPhoneNumber })
      });

      const data = await response.json();
      console.log('Force send OTP response:', data);

      if (!response.ok || !data.success) {
        console.error('Force OTP send failed:', data.message);
        setError(data.message || 'Failed to send OTP. Please try again.');
        setShowForceLoginModal(false);
        return;
      }

      console.log('Force OTP sent successfully!');
      const updated = incrementOtpAttempts(mobile);
      const remaining = OTP_MAX_ATTEMPTS - updated.count;
      
      // Close modal and show OTP screen
      setShowForceLoginModal(false);
      setSessionInfo(null);
      setShowOTP(true);
      toast.success(remaining > 0 ? `OTP sent! (${remaining} attempt${remaining !== 1 ? 's' : ''} left)` : 'OTP sent!');
    } catch (err) {
      console.error('Force login error:', err);
      setError('An error occurred. Please try again.');
      setShowForceLoginModal(false);
    } finally {
      setForceLoginLoading(false);
    }
  };

  // Close force login modal
  const handleCloseForceLoginModal = () => {
    setShowForceLoginModal(false);
    setSessionInfo(null);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    // Check OTP rate limit before proceeding
    const attempts = getOtpAttempts(mobile);
    if (attempts.count >= OTP_MAX_ATTEMPTS) {
      const elapsed = attempts.blockedAt ? Date.now() - attempts.blockedAt : 0;
      const minsLeft = Math.max(1, Math.ceil((OTP_BLOCK_MS - elapsed) / 60000));
      setError(`Too many OTP requests. Please wait ${minsLeft} minute${minsLeft !== 1 ? 's' : ''} before trying again.`);
      return;
    }

    setLoading(true);

    try {
      console.log('=== PHONE OTP LOGIN STARTED ===');
      console.log('Phone number (10 digit):', mobile);
      
      // Format phone number with +91 prefix
      const formattedPhoneNumber = `+91${mobile}`;
      console.log('Formatted phone number:', formattedPhoneNumber);
      console.log('Sending OTP via API...');
      
      const response = await fetch(`${API_BASE_URL}/users/auth/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phNum: formattedPhoneNumber })
      });

      const data = await response.json();
      console.log('OTP send response:', data);

      // Check if session already exists on another device
      if (data.sessionExists === true) {
        console.log('Session already exists, showing force login modal');
        setSessionInfo({
          phoneNumber: data.phoneNumber || formattedPhoneNumber,
          userId: data.userId
        });
        setShowForceLoginModal(true);
        setLoading(false);
        return;
      }

      if (!response.ok || !data.success) {
        console.error('OTP send failed:', data.message);
        setError(data.message || 'Failed to send OTP. Please try again.');
        setLoading(false);
        return;
      }

      console.log('OTP sent successfully!');
      const updated = incrementOtpAttempts(mobile);
      const remaining = OTP_MAX_ATTEMPTS - updated.count;
      setShowOTP(true);
      toast.success(remaining > 0 ? `OTP sent! (${remaining} attempt${remaining !== 1 ? 's' : ''} left)` : 'OTP sent!');
    } catch (err) {
      console.error('Unexpected error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-5 md:space-y-6 relative">
      {/* Force Login Modal */}
      <ForceLoginModal
        isOpen={showForceLoginModal}
        onClose={handleCloseForceLoginModal}
        onForceLogin={handleForceLogin}
        phoneNumber={sessionInfo?.phoneNumber}
        loading={forceLoginLoading}
      />

      {showOTP ? (
        <OTPVerification 
          mobile={mobile} 
          onLoginSuccess={onLoginSuccess}
          onBack={() => {
            setShowOTP(false);
          }}
        />
      ) : (
        <form onSubmit={handleSendOTP} className="space-y-4 sm:space-y-5 relative">

      {/* Loading Bar */}
      {loading && (
        <div className="absolute -top-4 left-0 right-0 h-1 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-blue-500 animate-[loadingBar_2s_linear]" style={{animation: 'loadingBar 2s linear', animationFillMode: 'forwards'}} />
        </div>
      )}
      <style>{`
        @keyframes loadingBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <div>
        <label className="block text-xs sm:text-sm font-medium text-slate-600 mb-1.5 sm:mb-2">
          Mobile Number
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm sm:text-base font-medium">
            +91
          </span>
          <input
            type="tel"
            value={mobile}
            onChange={(e) => {
              // Only allow numbers and limit to 10 digits
              const value = e.target.value.replace(/\D/g, '').slice(0, 10);
              setMobile(value);
              setError('');
            }}
            className="w-full pl-14 pr-4 py-2.5 sm:py-3 text-sm sm:text-base border border-slate-200 rounded-2xl bg-slate-50 text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none focus:bg-white"
            placeholder="Mobile Number (10 digits)"
            pattern="[0-9]{10}"
            maxLength="10"
            required
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || mobile.length !== 10 || getOtpAttempts(mobile).count >= OTP_MAX_ATTEMPTS}
        className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-bold text-base py-3 px-4 rounded-2xl transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
      >
        {loading ? 'Sending OTP...' : (
          <>
            Send OTP
            <ArrowRight size={18} />
          </>
        )}
      </button>
        </form>
      )}
    </div>
  );
};

export default MobileLoginTab;

